import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from "react-redux";
import Loader from "../../components/Loader";
import { convertDateToLongString, convertDataToTime } from "../../tools/convertDateToString";
import clientsApi from "../../store/clientsStore/clientsApi";
import styles from './UploadDetails.module.scss';

const UploadDetails = () => {
    const { uploadId } = useParams();
    const history = useHistory();
    const { info } = useSelector(s => s.branchReducer);
    const [uploadData, setUploadData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUploadDetails = async () => {
            if (!info?.id || !uploadId) return;
            
            try {
                setIsLoading(true);
                setError(null);
                // Используем новый эндпоинт для получения одной загрузки
                const uploadData = await clientsApi.getSingleUploadXLSX(info.id, uploadId);
                
                // Проверяем, есть ли upload_id в данных (основное поле для проверки)
                if (uploadData && uploadData.data.upload_id) {
                    setUploadData(uploadData.data);
                } else {
                    setError('Загрузка не найдена');
                }
            } catch (error) {
                console.error('Ошибка при загрузке деталей:', error);
                if (error.response?.status === 404) {
                    setError('Загрузка не найдена');
                } else if (error.response?.status === 403) {
                    setError('Нет доступа к данной загрузке');
                } else {
                    setError('Ошибка при загрузке данных');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchUploadDetails();
    }, [info, uploadId]);

    const renderStatusBadge = (upload) => {
        const { status, completed_at } = upload;
        
        // Если статус completed - завершено успешно
        if (status === 'completed') {
            return (
                <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>
                    Завершено
                </span>
            );
        }
        
        // Если нет completed_at, значит еще обрабатывается
        if (!completed_at) {
            return (
                <span className={`${styles.statusBadge} ${styles.statusProcessing}`}>
                    Обрабатывается
                </span>
            );
        }
        
        // Иначе это ошибка
        return (
            <span className={`${styles.statusBadge} ${styles.statusError}`}>
                Ошибка
            </span>
        );
    };

    const renderPhoneErrors = (phoneErrors) => {
        if (!phoneErrors || phoneErrors.length === 0) {
            return (
                <div className={styles.emptySection}>
                    <p>Ошибок в номерах телефонов не найдено</p>
                </div>
            );
        }

        return (
            <div className={styles.phoneErrorsTable}>
                <table className={styles.detailTable}>
                    <thead>
                        <tr>
                            <th>Номер телефона</th>
                            <th>Строка</th>
                            <th>Ошибка</th>
                        </tr>
                    </thead>
                    <tbody>
                        {phoneErrors.map((phoneError, index) => (
                            <tr key={index}>
                                <td className={styles.phoneCell}>{phoneError.phone}</td>
                                <td className={styles.numberCell}>{phoneError.row_number}</td>
                                <td className={styles.errorCell}>{phoneError.error}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPhoneDuplicates = (phoneDuplicates) => {
        if (!phoneDuplicates || phoneDuplicates.length === 0) {
            return (
                <div className={styles.emptySection}>
                    <p>Дублирующихся номеров телефонов не найдено</p>
                </div>
            );
        }

        return (
            <div className={styles.phoneDuplicatesTable}>
                <table className={styles.detailTable}>
                    <thead>
                        <tr>
                            <th>Номер телефона</th>
                            <th>Строки</th>
                            <th>Количество</th>
                        </tr>
                    </thead>
                    <tbody>
                        {phoneDuplicates.map((phoneDuplicate, index) => (
                            <tr key={index}>
                                <td className={styles.phoneCell}>{phoneDuplicate.phone}</td>
                                <td className={styles.rowNumbersCell}>
                                    {Array.isArray(phoneDuplicate.row_numbers) 
                                        ? phoneDuplicate.row_numbers.join(', ')
                                        : phoneDuplicate.row_numbers || '-'
                                    }
                                </td>
                                <td className={styles.countCell}>
                                    {Array.isArray(phoneDuplicate.row_numbers) 
                                        ? phoneDuplicate.row_numbers.length
                                        : 1
                                    }
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderMissingColumns = (missingColumns) => {
        if (!missingColumns || Object.keys(missingColumns).length === 0) {
            return (
                <div className={styles.emptySection}>
                    <p>Все необходимые колонки присутствуют</p>
                </div>
            );
        }

        return (
            <div className={styles.missingColumnsTable}>
                <table className={styles.detailTable}>
                    <thead>
                        <tr>
                            <th>Ожидаемая колонка</th>
                            <th>Описание</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(missingColumns).map(([column, description]) => (
                            <tr key={column}>
                                <td className={styles.columnCell}>{column}</td>
                                <td>{description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>
                    <Loader color="#73D389" size={40} />
                    <p className={styles.loadingText}>Загрузка детальной информации...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorState}>
                    <p className={styles.errorText}>{error}</p>
                    <button 
                        className={styles.backButton}
                        onClick={() => history.push('/download/clients-database')}
                    >
                        Вернуться к списку
                    </button>
                </div>
            </div>
        );
    }

    if (!uploadData) {
        return (
            <div className={styles.container}>
                <div className={styles.errorState}>
                    <p className={styles.errorText}>Данные о загрузке не найдены</p>
                    <button 
                        className={styles.backButton}
                        onClick={() => history.push('/download/clients-database')}
                    >
                        Вернуться к списку
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <button 
                        className={styles.backButton}
                        onClick={() => history.push('/download/clients-database')}
                    >
                        ← Вернуться к списку
                    </button>
                    <h1 className={styles.title}>Детали загрузки #{uploadData.upload_id}</h1>
                </div>
            </div>

            <div className={styles.content}>
                {/* Основная информация */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Основная информация</h2>
                    <div className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                            <label>Статус</label>
                            <div>{renderStatusBadge(uploadData)}</div>
                        </div>
                        <div className={styles.infoItem}>
                            <label>Дата начала</label>
                            <div className={styles.dateValue}>
                                {convertDateToLongString(uploadData.created_at)} {convertDataToTime(uploadData.created_at)}
                            </div>
                        </div>
                        <div className={styles.infoItem}>
                            <label>Дата завершения</label>
                            <div className={styles.dateValue}>
                                {uploadData.completed_at 
                                    ? `${convertDateToLongString(uploadData.completed_at)} ${convertDataToTime(uploadData.completed_at)}`
                                    : <span className={styles.processingText}>В процессе...</span>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Статистика обработки */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Статистика обработки</h2>
                    <div className={styles.statsGrid}>
                        <div className={`${styles.statCard} ${styles.totalStat}`}>
                            <div className={styles.statNumber}>{uploadData.total_rows || 0}</div>
                            <div className={styles.statLabel}>Всего строк</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.validStat}`}>
                            <div className={styles.statNumber}>{uploadData.valid_rows || 0}</div>
                            <div className={styles.statLabel}>Корректные строки</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.insertedStat}`}>
                            <div className={styles.statNumber}>{uploadData.inserted || 0}</div>
                            <div className={styles.statLabel}>Добавлено</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.updatedStat}`}>
                            <div className={styles.statNumber}>{uploadData.updated || 0}</div>
                            <div className={styles.statLabel}>Обновлено</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.skippedStat}`}>
                            <div className={styles.statNumber}>{uploadData.skipped || 0}</div>
                            <div className={styles.statLabel}>Пропущено</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.errorStat}`}>
                            <div className={styles.statNumber}>{uploadData.errors || 0}</div>
                            <div className={styles.statLabel}>Ошибки</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.duplicatesStat}`}>
                            <div className={styles.statNumber}>{uploadData.duplicates || 0}</div>
                            <div className={styles.statLabel}>Дубликаты</div>
                        </div>
                        <div className={`${styles.statCard} ${styles.invalidFormatStat}`}>
                            <div className={styles.statNumber}>{uploadData.invalid_format || 0}</div>
                            <div className={styles.statLabel}>Неверный формат</div>
                        </div>
                    </div>
                </div>

                {/* Сообщение об ошибке */}
                {uploadData.error_message && (
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Сообщение об ошибке</h2>
                        <div className={styles.errorMessage}>
                            {uploadData.error_message}
                        </div>
                    </div>
                )}

                {/* Инструкции - показываем стандартную инструкцию, если не передана специальная */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Инструкции</h2>
                    <div className={styles.instructionText}>
                        {uploadData.instruction || 
                        `Загружаемый файл должен быть в формате .xlsx и содержать следующие колонки:
phone (обязательно) — номер телефона
first_name (опционально) — имя
last_name (опционально) — фамилия
birthdate (опционально) — дата рождения в формате ДД-ММ-ГГГГ
balance (опционально) — баланс

Убедитесь, что заголовки колонок в файле точно соответствуют указанным именам.`}
                    </div>
                </div>

                {/* Отсутствующие колонки */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Отсутствующие колонки</h2>
                    {renderMissingColumns(uploadData.missing_columns)}
                </div>

                {/* Ошибки в номерах телефонов */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Ошибки в номерах телефонов</h2>
                    {renderPhoneErrors(uploadData.phone_errors)}
                </div>

                {/* Дублирующиеся номера телефонов */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Дублирующиеся номера телефонов</h2>
                    {renderPhoneDuplicates(uploadData.phone_duplicates)}
                </div>
            </div>
        </div>
    );
};

export default UploadDetails;